
/**
 * Normalizuje liczbę dziesiętną
 * @param {string|number} value
 * @returns {string}
 */
export const normalizeNumber = value =>  {
    const negative = String(value).substring(0, 1) === '-';
    value = String(value)
        .replace(/,/, '.')
        .replace(/[^\d.]+/g, '')

    const parts = value.split('.');
    if (parts.length <= 1) {
        return (negative ? '-' : '') + value;
    }
    const lastPart = parts.pop();
    return (negative ? '-' : '') + parts.join('') + '.' + lastPart;
};

/**
 * Usuwa ze stringa wszystkie znaki niebędące cyframi
 * @param value
 * @returns {string}
 */
export const ensureDigits = value => String(value).replace(/\D/g, '').trim();

/**
 * Filtruje wartość wejściową zwracając liczbę
 * @param {string|number} value
 * @returns {number}
 */
export const numberFilter = value =>  Number(normalizeNumber(value));

/**
 * Filtruje tablicę wejściową zwracając tablicę liczb
 * @param value
 * @return {Number[]}
 */
export const numberArrayFilter = value => {
    const filteredArray = [];
    if (value && typeof value === 'object' && typeof value[Symbol.iterator] === 'function') {
        for (let val of value) {
            filteredArray.push(numberFilter(val));
        }
    }
    return filteredArray;
}

/**
 * Formatuje liczbę walutę
 * @param value
 * @returns {string}
 */
export const formatCurrency = value => Number(value).toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

/**
 * Formatuje liczbę walutę
 * @param value
 * @param {number} minimumFractionDigits
 * @param {number} maximumFractionDigits
 * @returns {string}
 */
export const formatNumber = (value, minimumFractionDigits = 0, maximumFractionDigits = 0) => Number(value)
    .toLocaleString('pl-PL', {
        minimumFractionDigits: Number(minimumFractionDigits),
        maximumFractionDigits: Number(maximumFractionDigits),
    });

/**
 * Formatuje liczbę do minimalnej ilości miejsc dziesiętnych
 * @param value
 * @param minDigits
 * @returns {string}
 */
export const ensureMinimumFractionDigits = (value, minDigits = 2) => {
    const [integers, digits] = normalizeNumber(value).split('.');
    return `${integers}.${_.padEnd(digits || 0, minDigits, '0')}`;
};


// Numery kierunkowe sieci stacjonarnych w Polsce
const LANDLINE_PREFIXES = [
    '12', '13', '14', '15', '16', '17', '18', '22', '23', '24', '25', '29', '32', '33', '34', '41', '42', '43', '44',
    '46', '48', '52', '54', '55', '56', '58', '59', '61', '62', '63', '65', '67', '68', '71', '74', '75', '76', '77',
    '81', '82', '83', '84', '85', '86', '87', '89', '91', '94', '95'
];

const COUNTRY_CODE_PL = '0048';

/**
 * Parsuje numer telefonu. Zwraca obiekt z danymi
 * [!!] Tylko polskie numery!
 * @param phoneNumber
 * @return {{isInternal: boolean, number: string, isLandline: boolean, prefix: string, countryCode: string, isValid: boolean, raw: string, isMobile: boolean, isDomestic: boolean}}
 */
export const parsePhoneNumber = phoneNumber => {
    phoneNumber = String(phoneNumber).trim();
    if (phoneNumber.substring(0, 1) === '+') {
        phoneNumber = '00' + phoneNumber.substring(1);
    }

    let number = String(phoneNumber).replace(/\D/g, '');
    let isValid = number.length > 3;
    let countryCode = COUNTRY_CODE_PL;
    if (number.substring(0, 2) === '00') {
        countryCode = number.substring(0, 4);
    }

    const isDomestic = countryCode === COUNTRY_CODE_PL;
    const prefix = isDomestic ? number.substring(0, 2) : '';
    const isLandline = LANDLINE_PREFIXES.indexOf(prefix) !== -1;

    return {
        number,
        prefix,
        countryCode,
        raw: phoneNumber,
        isInternal: number.length <= 3,
        isMobile: !isLandline,
        isLandline,
        isValid,
        isDomestic,
    };
};

/**
 * Formatuje numer telefonu do formatu 000-000-000 (dla numerów komórkowych) lub (00) 000-00-00 (dla stacjonarnych)
 * @param number
 * @returns {string}
 */
export const formatPhoneNumber = number => {
    const phoneNumber = parsePhoneNumber(number);

    // Numer wewnętrzny
    if (phoneNumber.isInternal) {
        return number;
    }

    // Numer stacjonarny
    if (phoneNumber.isLandline) {
        return phoneNumber.number.replace(/^(\d{2})(\d{3})(\d{2})(\d{2})$/g, '($1) $2-$3-$4');
    }

    if (phoneNumber.number.substring(0, 4) === phoneNumber.countryCode) {
        phoneNumber.number = phoneNumber.number.substring(4);
    }

    // Numer komórkowy
    return (phoneNumber.countryCode !== COUNTRY_CODE_PL ? `+${Number(phoneNumber.countryCode)} ` : '') + phoneNumber.number
        .replace(/(\d{3})/g, '$1-')
        .replace(/-$/, '');
};

/**
 * Formatuje numer konta bankowego
 * @param iban
 * @returns {string}
 */
export const formatIban = iban => {
    iban = String(iban).replace(/[^0-9]/g, '');
    if (iban.length === 0) {
        return '';
    }
    return iban.substr(0, 2) +
        ' ' +
        iban.substr(2).replace(/(.{4})(?!$)/g, "$1 ")
}

/**
 * Oblicza kwotę netto z brutto
 * @param {Number} grossPrice Kwota brutto
 * @param {Number} taxRate    Stawka podatku VAT
 * @returns {{gross: string, nett: string, tax: string, value: string, taxRate: *}}
 */
export const calculateVatNettPrice = (grossPrice, taxRate) => {
    const taxValue = Math.round((grossPrice * 100 * taxRate) / (100 + taxRate));
    const nett = (Math.round(grossPrice  * 100 - taxValue) / 100).toFixed(2);
    return {
        taxRate,
        nett,
        gross: Number(grossPrice).toFixed(2),
        tax: (taxValue / 100).toFixed(2),
        value: nett,
    };
};

/**
 * Oblicza kwotę brutto
 * @param {Number} nettPrice  Kwota netto
 * @param {Number} taxRate    Stawka podatku VAT
 * @returns {{taxRate: *, gross: string, nett: string, tax: string, value: string}}
 */
export const calculateVatGrossPrice = (nettPrice, taxRate) => {
    const taxValue = Math.round(nettPrice * 100 * (taxRate / 100));
    const gross = (Math.round(nettPrice  * 100 + taxValue) / 100).toFixed(2);
    return {
        taxRate,
        gross,
        nett: Number(nettPrice).toFixed(2),
        tax: (taxValue / 100).toFixed(2),
        value: gross,
    };
};
